/**
 * Client detection flags.
 *
 * Each flag is `true` when the current hostname matches a known client.
 * The `client` slug is derived from the hostname in auth.js and made
 * available as a global — this module reads it once at load time.
 *
 * Usage:
 *   import { isAlera, isFish } from './helpers/client-flags.js';
 */

// Resolve client slug from the global set by auth.js at startup.
// Falls back to hostname parsing when running as a bundled module.
function resolveClient() {
	if (typeof client !== 'undefined') return client;
	const hn = window.location.hostname;
	if (hn === 'localhost' || hn === 'www.staging.nthround.io') {
		const match = document.cookie.match(/nth\.TestClient=([^;]+)/);
		if (match) return match[1];
	}
	return hn === 'localhost' ? 'www.staging' : hn.split('.').slice(0, -2).join('.');
}

const _client = resolveClient();

export const is1251 = ['1251capital'].includes(_client);
export const is3MG = ['3rdmg'].includes(_client);
export const isACC = ['accencio'].includes(_client);
export const isACR = ['acropolisdev'].includes(_client);
export const isAES = ['aestudio'].includes(_client);
export const isAND = ['andesiteholdings'].includes(_client);
export const isARC = ['arcfund', 'historicpreservation'].includes(_client);
export const isASP = ['asplundh'].includes(_client);
export const isASPT = ['asplundhtest'].includes(_client);
export const isAV = ['agent-vista'].includes(_client);
export const isBB = ['bushbros', 'onwardfe'].includes(_client);
export const isBW = ['barry-wehmiller'].includes(_client);
export const isBOH = ['bohler'].includes(_client);
export const isCER = ['ceritypartners'].includes(_client);
export const isEDM = ['edmundoptics', 'onwardfe'].includes(_client);
export const isETN = ['etnyre'].includes(_client);
export const isFF = ['foundingfarmers'].includes(_client);
export const isFAV = ['favfunds'].includes(_client);
export const isGDC = ['greendatacenters'].includes(_client);
export const isGG = ['gardnerglobal'].includes(_client);
export const isGPG = ['gpgventures'].includes(_client);
export const isH5H = ['high5health'].includes(_client);
export const isHTC = ['hightidecapital'].includes(_client);
export const isIQ3 = ['iq3'].includes(_client);
export const isJMH = ['jmhcapital'].includes(_client);
export const isJMS = ['jmsearch'].includes(_client);
export const isLT = ['lucastree'].includes(_client);
export const isLLProducts = ['llproducts'].includes(_client);
export const isMAR = ['marvin', 'marvintest'].includes(_client);
export const isMAZ = ['mazzetti'].includes(_client);
export const isMH = ['maguirehayden'].includes(_client);
export const isNMS = ['newmarket'].includes(_client);
export const isNTH = ['www'].includes(_client);
export const isONW = ['onward'].includes(_client);
export const isORR = ['orrick'].includes(_client);
export const isPC = ['prodoscapital'].includes(_client);
export const isPHJ = ['pharmajet'].includes(_client);
export const isPSS = ['psands'].includes(_client);
export const isRAD = ['radian'].includes(_client);
export const isROK = ['rokk3r'].includes(_client);
export const isSEQ = ['sequoia-financial'].includes(_client);
export const isSTK = ['stockton'].includes(_client);
export const isTBC = ['thebrickcompanies'].includes(_client);
export const isTCA = ['theclosingagent', 'theclosingagentllc'].includes(_client);
export const isTSG = ['thesymmetricalgroup'].includes(_client);
export const isUA = ['urbanalpha'].includes(_client);
export const isUSV = ['usventure'].includes(_client);
export const isVMS = ['onwardpe', 'activatecp'].includes(_client);
export const isVERM = ['vermeer'].includes(_client);

// Key multi-client flags
export const isAlera = ['aleragroup'].includes(_client);
export const isFish = ['fishbeck'].includes(_client);
export const isSheetz = ['sheetz'].includes(_client);
export const isStaging = ['www.staging'].includes(_client);
export const isUllico = ['ullico'].includes(_client);
export const isVestria = ['vestriacapital'].includes(_client);

// Shared balance/quantity comparison tolerance -- one source of truth so GT's
// matching engine and the Tranche Scan Report can't drift out of sync.
//
// Rounding/aggregation drift scales with the size of the position being
// compared, not with which client owns it -- a per-client whitelist
// (isUllico, isMAR, isUSV, isBW) kept growing as more showed up. Derive the
// allowance from the actual magnitude instead. Calibrated against Ullico's
// ~750k-share account (eyeballed at 0.02) and biased loose on purpose: better
// to skip a few small mismatches than spend hours chasing rounding noise.
//
// The floor itself has to scale down for clients tracked at finer precision --
// Brick (isTBC) runs to the millionth (see roundToFour() in auth.js), so a flat
// 0.02 floor would swallow its real share-quantity differences as "noise". For
// everyone else, 0.001 turned out tighter than the ~0.02 this file was originally
// calibrated to above -- floating-point drift on ordinary fractional-share
// balances (observed up to ~0.01 share) was getting flagged as a mismatch.
const minTrackedUnit = isTBC ? 0.000001 : 0.0001; // smallest quantity this client tracks distinctly
export const balanceEpsilon = isTBC ? minTrackedUnit * 10 : 0.02; // fallback floor when no values are available to scale from
export function balanceTolerance(...values) {
	const scale = Math.max(1, ...values.map((v) => Math.abs(Number(v)) || 0));
	return Math.max(balanceEpsilon, scale * 1e-7);
}
export function nearlyEqualBalance(a, b) {
	return Math.abs(Number(a) - Number(b)) <= balanceTolerance(a, b);
}

// #6144a: looser, report-only tolerance for the Tranche Scan Report's tranche-sum-mismatch
// signal. NOT for the matching engine (quantitiesMatch/nearlyEqualBalance above pair and score
// actual transactions and must stay tight) -- a real, large tranche can drift by rounding/
// aggregation noise well past balanceEpsilon without indicating a real problem, so this scales
// to 1% of the tranche size, keeping balanceEpsilon only as the floor for small tranches.
export function trancheSumTolerance(...values) {
	const scale = Math.max(1, ...values.map((v) => Math.abs(Number(v)) || 0));
	return Math.max(balanceEpsilon, scale * 0.01);
}
export function nearlyEqualTrancheSum(a, b) {
	return Math.abs(Number(a) - Number(b)) <= trancheSumTolerance(a, b);
}

// #6144b: Tranche Analysis inclusion, by each transaction's own Security Type (not by class --
// one class can carry mixed types). Deliberately conservative: only a transaction whose Security
// Type is BOTH set AND one of our own recognized-but-not-yet-included types is excluded. A blank/
// unset Security Type (legacy data, or a client that never populates it) or any value outside our
// own recognized list (a typo, a custom/free-text string) is always included -- we only exclude
// what we're confident is one of the untraced-mechanics types, never on an absent or unknown value.
export const TRANCHE_INCLUDED_SECURITY_TYPES = ['Ordinary Shares', 'Restricted Shares', 'Partnership Interest'];
export const ALL_RECOGNIZED_SECURITY_TYPES = [
	'Ordinary Shares',
	'Restricted Shares',
	'RSUs',
	'Profits Interest',
	'SARs',
	'Options',
	'Warrants',
	'Convertibles',
	'Debt',
	'Capital Contribution',
	'Partnership Interest',
];
export function isTrancheEligibleSecurityType(trans) {
	const t = trans?.securityType;
	if (!t) return true; // never set -- include
	if (TRANCHE_INCLUDED_SECURITY_TYPES.includes(t)) return true;
	if (!ALL_RECOGNIZED_SECURITY_TYPES.includes(t)) return true; // not one of ours -- include
	return false; // a recognized type not yet on the include list (RSUs, SARs, Options, ...)
}
